import React, { ReactNode } from 'react';
import parse from 'html-react-parser';
import IconsHolder from '@ecs/tng-common/components/icons-holder/IconsHolder';
import { scrollTo, ifMobile } from '@ecs/tng-common/scripts/helpers';
import {
  CollapseECS,
  CollapseECSBody,
  CollapseECSButton
} from '@ecs/tng-common/components/collapse/CollapseEcs';

export interface ITableOfContentsContent {
  title: string;
  id: string;
}
export function formTOCLinks(content: string): ITableOfContentsContent[] {
  const sections: ITableOfContentsContent[] = [];
  if (content) {
    const parsedContent = parse(content);
    const traverse = (element: ReactNode): void => {
      if (Array.isArray(element)) {
        element.forEach(traverse);
      } else if (React.isValidElement(element)) {
        if (element.type === 'section' && element?.props?.id?.startsWith('s')) {
          const title: string[] = [];
          if (element.props['data-toc']) {
            title.push(element.props['data-toc']);
          } else {
            const h2Element = React.Children.toArray(element?.props?.children).find(
              (child) => React.isValidElement(child) && child.type === 'h2'
            ) as React.ReactElement<{ children: ReactNode }> | undefined;
            // Handle case where h2Element.props.children has typeof Symbol with props children of its own
            if (h2Element?.props?.children) {
              if (typeof h2Element.props.children === 'string') {
                title.push(h2Element.props.children);
              } else if (Array.isArray(h2Element.props.children)) {
                h2Element.props.children.forEach((child) => {
                  if (typeof child === 'string') {
                    title.push(child);
                  } else if (typeof child === 'object' && child.props?.children) {
                    // if type is sup, create a sup element with the children
                    if (child.type === 'sup') {
                      title.push(`<sup>${child.props.children}</sup>`);
                    } else {
                      title.push(child.props.children);
                    }
                  }
                });
              }
            }
          }
          sections.push({
            title: title.join(''),
            id: element.props?.id
          });
        } else if (element.props && element.props.children) {
          traverse(element.props.children);
        }
      }
    };

    traverse(parsedContent);
  }
  return sections;
}

interface ITableOfContentsProps {
  content: ITableOfContentsContent[];
  // categoryId: number | string | undefined;
  // dataId: number | string;
  className?: string;
}
interface ITableOfContentsState {
  activeSection: string;
}

class TableOfContents extends React.Component<ITableOfContentsProps, ITableOfContentsState> {
  observer: IntersectionObserver | null;

  constructor(props: ITableOfContentsProps) {
    super(props);
    this.state = {
      activeSection: ''
    };
    this.observer = null;
  }

  componentDidMount() {
    this.initializeObserver();
  }

  componentWillUnmount() {
    this.setState({ activeSection: '' });
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initializeObserver = () => {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        let anyIntersecting = false;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setState({
              activeSection: entry.target.id
            });
            anyIntersecting = true;
          }
        });
        if (!anyIntersecting) {
          this.setState({
            activeSection: ''
          });
        }
      },
      { rootMargin: `${ifMobile() ? '24' : '32'}px 0px -100% 0px`, threshold: 0 }
    );

    this.props.content.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element && this.observer) {
        this.observer.observe(element);
      }
    });

    return () => {
      this.props.content.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element && this.observer) {
          this.observer.unobserve(element);
        }
      });
    };
  };

  scrollToSection(id: string) {
    this.setState({
      activeSection: id
    });
    scrollTo(id, 1);
  }

  render() {
    const { content } = this.props;
    const numOfArticlesToShowinTOC = content.length <= 6 ? 6 : 5;
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {content.length > 0 ? (
          <nav
            aria-labelledby="toc-heading"
            className={`${this.props.className || ''} col-12 col-lg-4 side-rail mb-lg-6 mb-lg-0 sticky-lg-top`}>
            <div className="d-flex align-items-center mb-4">
              <IconsHolder
                icon="ecs-font-icon-con-news"
                variant="primary"
                size="md"
              />
              <p
                id="toc-heading"
                className="eyebrow-text ms-4">
                In this article
              </p>
            </div>
            <ul
              className="list-unstyled ms-3 mb-0"
              id="toc-nav">
              {content
                .slice(0, numOfArticlesToShowinTOC)
                .map((element: ITableOfContentsContent) => (
                  <li
                    key={element.id}
                    className="mb-0">
                    <button
                      type="button"
                      onClick={() => {
                        this.scrollToSection(element.id);
                      }}
                      /* data-link-cs={`ae-mod-toc-c-${this.props.categoryId}-${this.props.dataId}-k${index + 1}`} */
                      title={element.title}
                      className={`${this.state.activeSection === element.id ? 'active' : ''} btn-reset h7 text-gray py-2 d-inline-block mb-0 ps-4 ps-md-5`}>
                      {parse(element.title)}
                    </button>
                  </li>
                ))}
            </ul>

            {content.length > 5 && numOfArticlesToShowinTOC === 5 ? (
              <CollapseECS id="show-more">
                <CollapseECSBody>
                  <ul className="list-unstyled ms-3 mb-0">
                    {content
                      .slice(numOfArticlesToShowinTOC)
                      .map((element: ITableOfContentsContent) => (
                        <li
                          key={element.id}
                          className="mb-0">
                          <button
                            type="button"
                            onClick={() => {
                              this.scrollToSection(element.id);
                            }}
                            /* data-link-cs={`ae-mod-toc-c-${this.props.categoryId}-${this.props.dataId}-k${index + 1}`} */
                            title={element.title}
                            className={`${this.state.activeSection === element.id ? 'active' : ''} btn-reset h7 text-gray py-2 d-inline-block mb-0 ps-4 ps-md-5`}>
                            {parse(element.title)}
                          </button>
                        </li>
                      ))}
                  </ul>
                </CollapseECSBody>
                <CollapseECSButton className="btn-link ps-4 ps-md-5 py-2 h7 mb-0 ms-3 d-lg-none">
                  <span className="collapse-closed-control align-items-center ">
                    View all <span className="ecs-font-icon-nav-angle-down font-icon-size-md" />
                  </span>
                  <span className="collapse-open-control align-items-center">
                    View less <span className="ecs-font-icon-nav-angle-up font-icon-size-md" />
                  </span>
                </CollapseECSButton>
              </CollapseECS>
            ) : null}
          </nav>
        ) : null}
      </>
    );
  }
}
export default TableOfContents;
